import { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/AdminLayout";
import "./ScreenConfig.css";

const API_BASE = process.env.REACT_APP_API_URL;
const ORG_ID   = "resh-dev";

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    OrgId: ORG_ID,
  };
}

// ─── Snackbar ─────────────────────────────────────────────────────────────────
function useSnackbar() {
  const [snack, setSnack] = useState(null);
  const timerRef = useRef();
  const showSnack = useCallback((msg, type = "error") => {
    clearTimeout(timerRef.current);
    setSnack({ msg, type, id: Date.now() });
    timerRef.current = setTimeout(() => setSnack(null), 4500);
  }, []);
  const dismissSnack = useCallback(() => { clearTimeout(timerRef.current); setSnack(null); }, []);
  return { snack, showSnack, dismissSnack };
}

function Snackbar({ snack, onDismiss }) {
  if (!snack) return null;
  return (
    <div key={snack.id} className={`sc-snackbar sc-snackbar--${snack.type}`} role="alert">
      <span className="sc-snackbar__msg">{snack.msg}</span>
      <button className="sc-snackbar__close" onClick={onDismiss}>✕</button>
    </div>
  );
}

// ─── Inline editable text ─────────────────────────────────────────────────────
function EditableText({ value, onChange, tag: Tag = "span", className, placeholder }) {
  const [editing, setEditing] = useState(false);
  const ref = useRef();

  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  if (editing) {
    return (
      <textarea
        ref={ref}
        className={`sc-editable-input ${className || ""}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
        rows={Math.max(2, (value || "").split("\n").length)}
      />
    );
  }

  return (
    <Tag
      className={`sc-editable ${className || ""}`}
      onClick={() => setEditing(true)}
      title="Click to edit"
    >
      {value || <span className="sc-editable__placeholder">{placeholder || "Click to edit…"}</span>}
      <span className="sc-editable__pen" aria-hidden="true">✏</span>
    </Tag>
  );
}

// ─── Clickable image slot ─────────────────────────────────────────────────────
function ImageSlot({ src, onPick, label, aspectRatio = "16/9" }) {
  const [errored, setErrored] = useState(false);

  // Reset error state whenever src changes
  useEffect(() => { setErrored(false); }, [src]);

  return (
    <div
      className="sc-img-slot"
      style={{ aspectRatio }}
      onClick={onPick}
      title="Click to change image"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onPick()}
    >
      {src && !errored ? (
        <>
          <img
            src={src}
            alt={label || "section"}
            className="sc-img-slot__img"
            onError={() => setErrored(true)}
          />
          <div className="sc-img-slot__overlay">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>Change Image</span>
          </div>
        </>
      ) : src && errored ? (
        /* 404 / broken image */
        <div className="sc-img-slot__deleted">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="28" height="28" style={{ opacity: 0.4 }}>
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="sc-img-slot__deleted-msg">This file may be deleted.</span>
          <span className="sc-img-slot__deleted-hint">Click to replace</span>
        </div>
      ) : (
        <div className="sc-img-slot__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="32" height="32" style={{ opacity: 0.3 }}>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
          <span>{label || "Click to add image"}</span>
        </div>
      )}
    </div>
  );
}

// ─── Gallery image picker drawer ──────────────────────────────────────────────
const VIDEO_EXTS_CHECK = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
const isVideoUrl = (url = "") => VIDEO_EXTS_CHECK.some((e) => url.toLowerCase().includes(e));

// Reusable searchable dropdown (same UI as Gallery's CategoryDropdown)
function PickerDropdown({ options, value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const all     = ["All", ...options];
  const filtered = all.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const isEmpty  = value === "All";

  return (
    <div className="cat-dropdown sc-picker-dropdown" ref={ref}>
      <button
        type="button"
        className={`cat-dropdown__trigger${open ? " cat-dropdown__trigger--open" : ""}${isEmpty ? " cat-dropdown__trigger--placeholder" : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="cat-dropdown__trigger-label">{value}</span>
        <svg className="cat-dropdown__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="cat-dropdown__panel">
          <div className="cat-dropdown__search-wrap">
            <svg className="cat-dropdown__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input className="cat-dropdown__search" type="text" placeholder="Search category…"
              value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>
          <ul className="cat-dropdown__list">
            {filtered.length === 0 ? (
              <li className="cat-dropdown__empty">No results</li>
            ) : filtered.map((opt) => (
              <li key={opt}
                className={`cat-dropdown__option${value === opt ? " cat-dropdown__option--active" : ""}`}
                onClick={() => { onChange(opt); setOpen(false); setSearch(""); }}>
                <span>{opt}</span>
                {value === opt && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ImagePickerDrawer({ open, onClose, onSelect }) {
  const [images,        setImages]        = useState([]);
  const [functionTypes, setFunctionTypes] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [category,      setCategory]      = useState("All");

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setCategory("All");
    setLoading(true);

    // Fetch function_types and gallery images in parallel
    Promise.all([
      fetch(`${API_BASE}/entities/filter/function_types`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ sort: [{ colId: "name", sort: "asc" }] }),
      }).then((r) => r.json()),
      fetch(`${API_BASE}/admin/gallery/list`, { headers: authHeaders() }).then((r) => r.json()),
    ])
      .then(([ftData, galData]) => {
        const types = ftData?.data?.[0]?.response ?? [];
        setFunctionTypes(types);
        const imgs = (galData.data || []).filter((img) => !isVideoUrl(img.url || ""));
        setImages(imgs);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const categoryOptions = functionTypes.map((ft) => ft.name);

  const filtered = images.filter((img) => {
    const matchCat  = category === "All" || img.category === category;
    const matchText = !search || (img.title || img.file_name || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchText;
  });

  return (
    <div className="sc-drawer__overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sc-drawer">

        {/* Head */}
        <div className="sc-drawer__head">
          <h3 className="sc-drawer__title">Pick an Image</h3>
          <button className="sc-drawer__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search + category filter row */}
        <div className="sc-drawer__toolbar">
          <div className="sc-drawer__search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ opacity: 0.45, flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="sc-drawer__search"
              type="text"
              placeholder="Search images…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {categoryOptions.length > 0 && (
            <PickerDropdown
              options={categoryOptions}
              value={category}
              onChange={setCategory}
            />
          )}
        </div>

        {/* Image grid */}
        <div className="sc-drawer__grid">
          {loading ? (
            <p className="sc-drawer__empty">Loading gallery…</p>
          ) : filtered.length === 0 ? (
            <p className="sc-drawer__empty">No images found.</p>
          ) : filtered.map((img) => (
            <button
              key={img.id}
              className="sc-drawer__img-btn"
              onClick={() => { onSelect(img.url); onClose(); }}
              title={img.title || img.file_name}
            >
              <img src={img.url} alt={img.title || img.file_name} className="sc-drawer__img" loading="lazy" />
              <span className="sc-drawer__img-label">{(img.title || img.file_name || "").slice(0, 22)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Gallery video picker drawer ─────────────────────────────────────────────
function VideoPickerDrawer({ open, onClose, onSelect }) {
  const [videos,        setVideos]        = useState([]);
  const [functionTypes, setFunctionTypes] = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState("");
  const [category,      setCategory]      = useState("All");

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setCategory("All");
    setLoading(true);

    Promise.all([
      fetch(`${API_BASE}/entities/filter/function_types`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ sort: [{ colId: "name", sort: "asc" }] }),
      }).then((r) => r.json()),
      fetch(`${API_BASE}/admin/gallery/list`, { headers: authHeaders() }).then((r) => r.json()),
    ])
      .then(([ftData, galData]) => {
        const types = ftData?.data?.[0]?.response ?? [];
        setFunctionTypes(types);
        const all = (galData.data || []).filter((item) => isVideoUrl(item.url || ""));
        setVideos(all);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const categoryOptions = functionTypes.map((ft) => ft.name);

  const filtered = videos.filter((v) => {
    const matchCat  = category === "All" || v.category === category;
    const matchText = !search || (v.title || v.file_name || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchText;
  });

  return (
    <div className="sc-drawer__overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sc-drawer">

        {/* Head */}
        <div className="sc-drawer__head">
          <h3 className="sc-drawer__title">Pick a Video</h3>
          <button className="sc-drawer__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Search + category filter row */}
        <div className="sc-drawer__toolbar">
          <div className="sc-drawer__search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14" style={{ opacity: 0.45, flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="sc-drawer__search"
              type="text"
              placeholder="Search videos…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {categoryOptions.length > 0 && (
            <PickerDropdown
              options={categoryOptions}
              value={category}
              onChange={setCategory}
            />
          )}
        </div>

        {/* Video preview grid — mirrors image picker layout */}
        <div className="sc-drawer__grid">
          {loading ? (
            <p className="sc-drawer__empty">Loading videos…</p>
          ) : filtered.length === 0 ? (
            <p className="sc-drawer__empty">No videos found in gallery.</p>
          ) : filtered.map((v) => (
            <button
              key={v.id}
              className="sc-drawer__img-btn sc-vp-card"
              onClick={() => { onSelect(v.url); onClose(); }}
              title={v.title || v.file_name}
            >
              <div className="sc-vp-card__thumb">
                <video
                  className="sc-vp-card__video"
                  src={v.url}
                  preload="metadata"
                  muted
                  playsInline
                />
                <div className="sc-vp-card__play">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                </div>
              </div>
              <span className="sc-drawer__img-label">
                {(v.title || v.file_name || "").slice(0, 22)}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Video slot (click to pick from gallery) ──────────────────────────────────
function VideoSlot({ src, onPick, label }) {
  return (
    <div
      className="sc-video-slot"
      onClick={onPick}
      title="Click to pick video from gallery"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onPick()}
    >
      {src ? (
        <>
          <video
            className="sc-video-slot__preview"
            src={src}
            preload="metadata"
            muted
            playsInline
          />
          <div className="sc-video-slot__overlay">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <polygon points="23 7 16 12 23 17 23 7" />
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
            </svg>
            <span>Change Video</span>
          </div>
          <div className="sc-video-slot__label">
            <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10"><polygon points="5 3 19 12 5 21 5 3" /></svg>
            <span>{src.split("/").pop().slice(0, 30)}</span>
          </div>
        </>
      ) : (
        <div className="sc-video-slot__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="32" height="32" style={{ opacity: 0.3 }}>
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          <span>{label || "Click to pick video"}</span>
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SCREEN EDITORS
// Each editor receives `config` (the current JSON for that screen) and
// `onChange(path, value)` to update a nested field.
// ═════════════════════════════════════════════════════════════════════════════

function set(obj, path, value) {
  const keys = path.split(".");
  const clone = JSON.parse(JSON.stringify(obj));
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
  cur[keys[keys.length - 1]] = value;
  return clone;
}

function setArr(obj, arrPath, index, field, value) {
  const arr = arrPath.split(".").reduce((o, k) => o[k], obj);
  const newArr = arr.map((item, i) =>
    i === index ? { ...item, [field]: value } : item
  );
  return set(obj, arrPath, newArr);
}

// ── Home editor ───────────────────────────────────────────────────────────────
function HomeEditor({ config, onChange }) {
  const [drawer, setDrawer] = useState(null); // { path } | null

  const pick = (path) => setDrawer({ path });

  return (
    <div className="sc-editor">
      {/* HERO */}
      <div className="sc-section">
        <div className="sc-section__label">Hero</div>
        <ImageSlot
          src={config.hero?.image}
          onPick={() => pick("hero.image")}
          label="Hero background"
          aspectRatio="21/9"
        />
        <div className="sc-section__fields">
          <EditableText
            tag="h2"
            className="sc-field--heading"
            value={config.hero?.heading}
            placeholder="Hero heading…"
            onChange={(v) => onChange("hero.heading", v)}
          />
          <EditableText
            className="sc-field--sm"
            value={config.hero?.cta}
            placeholder="CTA button text…"
            onChange={(v) => onChange("hero.cta", v)}
          />
        </div>
      </div>

      {/* SERVICES */}
      <div className="sc-section">
        <div className="sc-section__label">Services</div>
        <div className="sc-services-grid">
          {(config.services || []).map((svc, i) => (
            <div key={i} className="sc-service-card">
              <ImageSlot
                src={svc.image}
                onPick={() => pick(`services.${i}.image`)}
                label={svc.title}
                aspectRatio="3/4"
              />
              <EditableText
                tag="h4"
                className="sc-field--sm-bold"
                value={svc.title}
                placeholder="Service title"
                onChange={(v) => onChange(`services.${i}.title`, v)}
              />
              <EditableText
                className="sc-field--xs"
                value={svc.description}
                placeholder="Description"
                onChange={(v) => onChange(`services.${i}.description`, v)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* PORTFOLIO */}
      <div className="sc-section">
        <div className="sc-section__label">Portfolio</div>
        <div className="sc-section__fields">
          <EditableText
            className="sc-field--sm"
            value={config.portfolio?.label}
            placeholder="Section label"
            onChange={(v) => onChange("portfolio.label", v)}
          />
          <EditableText
            tag="h3"
            className="sc-field--heading"
            value={config.portfolio?.heading}
            placeholder="Section heading"
            onChange={(v) => onChange("portfolio.heading", v)}
          />
        </div>
        <div className="sc-portfolio-grid">
          {(config.portfolio?.images || []).slice(0, 4).map((src, i) => (
            <ImageSlot
              key={i}
              src={src}
              onPick={() => pick(`portfolio.images.${i}`)}
              label={`Portfolio ${i + 1}`}
              aspectRatio="4/5"
            />
          ))}
        </div>
      </div>

      {/* TESTIMONIALS */}
      <div className="sc-section">
        <div className="sc-section__label">Testimonials</div>
        {(config.testimonials || []).map((t, i) => (
          <div key={i} className="sc-testimonial-row">
            <span className="sc-testimonial-num">{i + 1}</span>
            <div className="sc-testimonial-fields">
              <EditableText
                className="sc-field--sm"
                value={t.quote}
                placeholder="Quote"
                onChange={(v) => onChange(`testimonials.${i}.quote`, v)}
              />
              <EditableText
                className="sc-field--xs"
                value={t.name}
                placeholder="Name"
                onChange={(v) => onChange(`testimonials.${i}.name`, v)}
              />
              <EditableText
                className="sc-field--xs"
                value={t.role}
                placeholder="Role / event"
                onChange={(v) => onChange(`testimonials.${i}.role`, v)}
              />
            </div>
          </div>
        ))}
      </div>

      <ImagePickerDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        onSelect={(url) => {
          const path = drawer.path;
          // Handle array index paths like "portfolio.images.2"
          const parts = path.split(".");
          const last = parts[parts.length - 1];
          if (!isNaN(last)) {
            const arrPath = parts.slice(0, -1).join(".");
            const arr = arrPath.split(".").reduce((o, k) => o[k], config);
            const newArr = arr.map((v, idx) => idx === Number(last) ? url : v);
            onChange(arrPath, newArr);
          } else {
            onChange(path, url);
          }
        }}
      />
    </div>
  );
}

// ── Generic portal editor (wedding / outdoor / bride / groom) ─────────────────
function PortalEditor({ config, onChange, screenKey }) {
  const [drawer, setDrawer] = useState(null);
  const pick = (path) => setDrawer({ path });

  return (
    <div className="sc-editor">
      {/* HERO */}
      <div className="sc-section">
        <div className="sc-section__label">Hero</div>
        <ImageSlot
          src={config.hero?.image}
          onPick={() => pick("hero.image")}
          label="Hero image"
          aspectRatio="21/9"
        />
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.hero?.label} placeholder="Label (e.g. PORTFOLIO)" onChange={(v) => onChange("hero.label", v)} />
          <EditableText tag="h2" className="sc-field--heading" value={config.hero?.heading} placeholder="Heading" onChange={(v) => onChange("hero.heading", v)} />
          <EditableText className="sc-field--xs" value={config.hero?.sub} placeholder="Subtitle" onChange={(v) => onChange("hero.sub", v)} />
          <EditableText className="sc-field--xs" value={config.hero?.badge} placeholder="Badge text" onChange={(v) => onChange("hero.badge", v)} />
          <EditableText className="sc-field--xs" value={config.hero?.floatTag} placeholder="Floating tag" onChange={(v) => onChange("hero.floatTag", v)} />
        </div>
      </div>

      {/* STORY */}
      <div className="sc-section">
        <div className="sc-section__label">Story</div>
        <EditableText tag="h3" className="sc-field--heading" value={config.story?.heading} placeholder="Story heading" onChange={(v) => onChange("story.heading", v)} />
        <EditableText className="sc-field--sm" value={config.story?.body} placeholder="Story body text" onChange={(v) => onChange("story.body", v)} />
        <EditableText className="sc-field--xs sc-field--italic" value={config.story?.quote} placeholder='"Quote…"' onChange={(v) => onChange("story.quote", v)} />
      </div>

      {/* GALLERY */}
      <div className="sc-section">
        <div className="sc-section__label">Gallery Images</div>
        <div className="sc-gallery-grid">
          {(config.gallery || []).map((item, i) => (
            <div key={i} className="sc-gallery-item">
              <ImageSlot
                src={item.image}
                onPick={() => pick(`gallery.${i}.image`)}
                label={item.title || `Image ${i + 1}`}
                aspectRatio="3/4"
              />
              <EditableText className="sc-field--xs" value={item.tag}   placeholder="Tag"         onChange={(v) => onChange(`gallery.${i}.tag`,   v)} />
              <EditableText className="sc-field--xs" value={item.title} placeholder="Title"       onChange={(v) => onChange(`gallery.${i}.title`, v)} />
              <EditableText className="sc-field--xs" value={item.desc}  placeholder="Description" onChange={(v) => onChange(`gallery.${i}.desc`,  v)} />
            </div>
          ))}
          <button className="sc-add-btn" onClick={() => {
            const newGallery = [...(config.gallery || []), { id: Date.now(), image: "", tag: "", title: "", desc: "", layout: "full" }];
            onChange("gallery", newGallery);
          }}>+ Add Image</button>
        </div>
      </div>

      {/* SIGNATURE */}
      <div className="sc-section">
        <div className="sc-section__label">Signature / CTA</div>
        <EditableText className="sc-field--xs" value={config.signature?.label} placeholder="Label" onChange={(v) => onChange("signature.label", v)} />
        <EditableText tag="h3" className="sc-field--heading" value={config.signature?.heading} placeholder="Heading" onChange={(v) => onChange("signature.heading", v)} />
        <EditableText className="sc-field--sm" value={config.signature?.cta} placeholder="CTA button" onChange={(v) => onChange("signature.cta", v)} />
      </div>

      <ImagePickerDrawer
        open={!!drawer}
        onClose={() => setDrawer(null)}
        onSelect={(url) => {
          const path = drawer.path;
          // Handle gallery array: "gallery.2.image"
          const parts = path.split(".");
          if (parts.length === 3 && parts[0] === "gallery") {
            const idx = Number(parts[1]);
            const field = parts[2];
            const newGallery = (config.gallery || []).map((item, i) =>
              i === idx ? { ...item, [field]: url } : item
            );
            onChange("gallery", newGallery);
          } else {
            onChange(path, url);
          }
        }}
      />
    </div>
  );
}

// ── Footer editor ─────────────────────────────────────────────────────────────
function LinkListEditor({ items = [], onChange, placeholder = { label: "Label", href: "URL" }, showHandle = false }) {
  const update = (i, field, val) =>
    onChange(items.map((item, idx) => idx === i ? { ...item, [field]: val } : item));

  const add    = () => onChange([...items, { label: "", href: "", handle: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="sc-link-list">
      {items.map((item, i) => (
        <div key={i} className="sc-link-row">
          <input
            className="sc-link-input"
            type="text"
            placeholder={placeholder.label}
            value={item.label || ""}
            onChange={(e) => update(i, "label", e.target.value)}
          />
          {showHandle && (
            <input
              className="sc-link-input"
              type="text"
              placeholder="@handle"
              value={item.handle || ""}
              onChange={(e) => update(i, "handle", e.target.value)}
            />
          )}
          <input
            className="sc-link-input sc-link-input--url"
            type="text"
            placeholder={placeholder.href}
            value={item.href || ""}
            onChange={(e) => update(i, "href", e.target.value)}
          />
          <button type="button" className="sc-link-remove" onClick={() => remove(i)} aria-label="Remove">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      ))}
      <button type="button" className="sc-link-add" onClick={add}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
          <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        Add
      </button>
    </div>
  );
}

function FooterEditor({ config, onChange }) {
  return (
    <div className="sc-editor">

      {/* BRAND */}
      <div className="sc-section">
        <div className="sc-section__label">Brand</div>
        <div className="sc-section__fields">
          <EditableText
            tag="strong"
            className="sc-field--sm-bold"
            value={config.logo}
            placeholder="Studio name"
            onChange={(v) => onChange("logo", v)}
          />
          <EditableText
            className="sc-field--sm"
            value={config.tagline}
            placeholder="Tagline / short description"
            onChange={(v) => onChange("tagline", v)}
          />
        </div>
      </div>

      {/* CONTACT */}
      <div className="sc-section">
        <div className="sc-section__label">Contact</div>
        <div className="sc-section__fields">
          <label className="sc-inline-label">Email</label>
          <EditableText
            className="sc-field--xs"
            value={config.contact?.email}
            placeholder="contact@email.com"
            onChange={(v) => onChange("contact.email", v)}
          />
          <label className="sc-inline-label" style={{ marginTop: "0.75rem" }}>Contact Links</label>
          <LinkListEditor
            items={config.contact?.links || []}
            onChange={(links) => onChange("contact.links", links)}
            placeholder={{ label: "Label (e.g. WhatsApp)", href: "URL or /path" }}
          />
        </div>
      </div>

      {/* SOCIAL */}
      <div className="sc-section">
        <div className="sc-section__label">Social Media</div>
        <div className="sc-section__fields">
          <label className="sc-inline-label">Section label</label>
          <EditableText
            className="sc-field--xs"
            value={config.social?.label}
            placeholder="SOCIAL"
            onChange={(v) => onChange("social.label", v)}
          />
          <label className="sc-inline-label" style={{ marginTop: "0.75rem" }}>Links</label>
          <LinkListEditor
            items={config.social?.links || []}
            onChange={(links) => onChange("social.links", links)}
            placeholder={{ label: "Platform (e.g. Instagram)", href: "https://…" }}
          />
        </div>
      </div>

      {/* STUDIO LINKS */}
      <div className="sc-section">
        <div className="sc-section__label">Studio Links</div>
        <div className="sc-section__fields">
          <label className="sc-inline-label">Section label</label>
          <EditableText
            className="sc-field--xs"
            value={config.studio?.label}
            placeholder="STUDIO"
            onChange={(v) => onChange("studio.label", v)}
          />
          <label className="sc-inline-label" style={{ marginTop: "0.75rem" }}>Links</label>
          <LinkListEditor
            items={config.studio?.links || []}
            onChange={(links) => onChange("studio.links", links)}
            placeholder={{ label: "Label (e.g. About Us)", href: "/about" }}
          />
        </div>
      </div>

      {/* COPYRIGHT */}
      <div className="sc-section">
        <div className="sc-section__label">Copyright</div>
        <div className="sc-section__fields">
          <EditableText
            className="sc-field--xs"
            value={config.copyright}
            placeholder="© 2026 Studio Name. All rights reserved."
            onChange={(v) => onChange("copyright", v)}
          />
        </div>
      </div>

    </div>
  );
}
const DEFAULT_ABOUT_VISION_POINTS = [
  { icon: ".", title: "CANDID ELEGANCE", desc: "Moving beyond staged poses to capture genuine human connection." },
  { icon: ".", title: "COLOR ARCHIVE", desc: "Mastering the complex color palettes of South Asian festivities with precision." },
];

// ── About editor ──────────────────────────────────────────────────────────────
function AboutEditor({ config, onChange }) {
  const [drawer, setDrawer] = useState(null);
  const pick = (path) => setDrawer({ path });
  const visionPoints = config.vision?.points?.length ? config.vision.points : DEFAULT_ABOUT_VISION_POINTS;

  useEffect(() => {
    if (!config.vision?.points?.length) {
      onChange("vision.points", DEFAULT_ABOUT_VISION_POINTS.map((point) => ({ ...point })));
    }
  }, [config.vision?.points?.length, onChange]);

  return (
    <div className="sc-editor">

      {/* HERO */}
      <div className="sc-section">
        <div className="sc-section__label">Hero</div>
        <ImageSlot src={config.hero?.photographerImage} onPick={() => pick("hero.photographerImage")} label="Photographer photo" aspectRatio="16/6" />
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs"      value={config.hero?.tag}             placeholder="Tag label"          onChange={(v) => onChange("hero.tag",             v)} />
          <EditableText tag="h2" className="sc-field--heading" value={config.hero?.heading} placeholder="Heading"           onChange={(v) => onChange("hero.heading",         v)} />
          <EditableText className="sc-field--xs sc-field--italic" value={config.hero?.headingItalic} placeholder="Italic word" onChange={(v) => onChange("hero.headingItalic", v)} />
          <EditableText className="sc-field--xs"      value={config.hero?.headingSuffix}   placeholder="Heading suffix"     onChange={(v) => onChange("hero.headingSuffix",   v)} />
          <EditableText className="sc-field--sm"      value={config.hero?.body}            placeholder="Intro paragraph"    onChange={(v) => onChange("hero.body",            v)} />
          <EditableText className="sc-field--xs"      value={config.hero?.photographerName} placeholder="Photographer name" onChange={(v) => onChange("hero.photographerName", v)} />
        </div>
      </div>

      {/* VISION */}
      <div className="sc-section">
        <div className="sc-section__label">Vision</div>
        <ImageSlot src={config.vision?.image} onPick={() => pick("vision.image")} label="Vision image" aspectRatio="3/1" />
        <div className="sc-section__fields">
          <EditableText tag="h3" className="sc-field--heading" value={config.vision?.heading} placeholder="Vision heading" onChange={(v) => onChange("vision.heading", v)} />
          <EditableText className="sc-field--sm"               value={config.vision?.body}    placeholder="Vision body"    onChange={(v) => onChange("vision.body",    v)} />
        </div>
        {visionPoints.map((point, i) => (
          <div key={i} className="sc-testimonial-row">
            <span className="sc-testimonial-num">{point.icon || i + 1}</span>
            <div className="sc-testimonial-fields">
              <EditableText className="sc-field--xs"      value={point.icon}  placeholder="Point icon"        onChange={(v) => onChange(`vision.points.${i}.icon`,  v)} />
              <EditableText className="sc-field--sm-bold" value={point.title} placeholder="Point title"       onChange={(v) => onChange(`vision.points.${i}.title`, v)} />
              <EditableText className="sc-field--xs"      value={point.desc}  placeholder="Point description" onChange={(v) => onChange(`vision.points.${i}.desc`,  v)} />
            </div>
          </div>
        ))}
      </div>

      {/* PROCESS */}
      <div className="sc-section">
        <div className="sc-section__label">Process Steps</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs"      value={config.process?.tag}     placeholder="Section tag"     onChange={(v) => onChange("process.tag",     v)} />
          <EditableText tag="h3" className="sc-field--heading" value={config.process?.heading} placeholder="Process heading" onChange={(v) => onChange("process.heading", v)} />
        </div>
        {(config.process?.steps || []).map((step, i) => (
          <div key={i} className="sc-testimonial-row">
            <span className="sc-testimonial-num">{step.num || i + 1}</span>
            <div className="sc-testimonial-fields">
              <EditableText className="sc-field--sm-bold" value={step.title} placeholder="Step title"       onChange={(v) => onChange(`process.steps.${i}.title`, v)} />
              <EditableText className="sc-field--xs"      value={step.desc}  placeholder="Step description" onChange={(v) => onChange(`process.steps.${i}.desc`,  v)} />
            </div>
          </div>
        ))}
      </div>

      {/* QUOTE */}
      <div className="sc-section">
        <div className="sc-section__label">Quote</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--sm sc-field--italic" value={config.quote?.text}   placeholder='"Quote text…"' onChange={(v) => onChange("quote.text",   v)} />
          <EditableText className="sc-field--xs"                  value={config.quote?.author} placeholder="— Author"      onChange={(v) => onChange("quote.author", v)} />
        </div>
      </div>

      <ImagePickerDrawer open={!!drawer} onClose={() => setDrawer(null)}
        onSelect={(url) => { onChange(drawer.path, url); }} />
    </div>
  );
}

// ── Contact editor ────────────────────────────────────────────────────────────
function ContactEditor({ config, onChange }) {
  const [drawer, setDrawer] = useState(null);

  return (
    <div className="sc-editor">

      {/* HERO IMAGE */}
      <div className="sc-section">
        <div className="sc-section__label">Hero Image</div>
        <ImageSlot src={config.image} onPick={() => setDrawer({ path: "image" })} label="Contact hero image" aspectRatio="16/6" />
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.imageTag}     placeholder="Image tag"     onChange={(v) => onChange("imageTag",     v)} />
          <EditableText className="sc-field--sm" value={config.imageHeading} placeholder="Image heading" onChange={(v) => onChange("imageHeading", v)} />
        </div>
      </div>

      {/* CONTACT INFO */}
      <div className="sc-section">
        <div className="sc-section__label">Contact Info</div>
        <div className="sc-section__fields">
          <EditableText tag="h2" className="sc-field--heading" value={config.heading} placeholder="Page heading"        onChange={(v) => onChange("heading", v)} />
          <EditableText className="sc-field--sm" value={config.subtext} placeholder="Sub-text paragraph"               onChange={(v) => onChange("subtext", v)} />
          <EditableText className="sc-field--xs" value={config.email}   placeholder="Email address"                    onChange={(v) => onChange("email",   v)} />
          <EditableText className="sc-field--xs" value={config.phone}   placeholder="Phone number"                     onChange={(v) => onChange("phone",   v)} />
          <EditableText className="sc-field--xs" value={config.address} placeholder="Studio address (use \n for lines)" onChange={(v) => onChange("address", v)} />
        </div>
      </div>

      {/* MAP */}
      <div className="sc-section">
        <div className="sc-section__label">Map Embed URL</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.mapUrl} placeholder="Google Maps embed URL" onChange={(v) => onChange("mapUrl", v)} />
        </div>
      </div>

      {/* SOCIALS */}
      <div className="sc-section">
        <div className="sc-section__label">Social Channels</div>
        <div className="sc-section__fields">
          <p className="sc-field--xs" style={{ color: "var(--color-text-muted)", margin: 0 }}>Each row: Platform name + Handle + URL</p>
          <LinkListEditor
            items={(config.socials || []).map((s) => ({ label: s.name || s.label || "", handle: s.handle || "", href: s.href || "" }))}
            onChange={(items) => onChange("socials", items.map((i) => ({ name: i.label, handle: i.handle, href: i.href })))}
            placeholder={{ label: "Platform (e.g. Instagram)", href: "https://…" }}
            showHandle
          />
        </div>
      </div>

      <ImagePickerDrawer open={!!drawer} onClose={() => setDrawer(null)}
        onSelect={(url) => { onChange(drawer.path, url); }} />
    </div>
  );
}

// ── Wedding Video editor ───────────────────────────────────────────────────────
function WeddingVideoEditor({ config, onChange }) {
  const [videoDrawer, setVideoDrawer] = useState(null); // { path } for video picker

  const films = config.gallery?.films || [];

  const updateFilm = (i, field, val) => {
    const updated = films.map((f, idx) => idx === i ? { ...f, [field]: val } : f);
    onChange("gallery.films", updated);
  };

  const addFilm = () => {
    onChange("gallery.films", [
      ...films,
      { id: Date.now(), video: "", tag: "", title: "", span: "normal" },
    ]);
  };

  const removeFilm = (i) => {
    onChange("gallery.films", films.filter((_, idx) => idx !== i));
  };

  return (
    <div className="sc-editor">

      {/* HERO */}
      <div className="sc-section">
        <div className="sc-section__label">Hero</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.hero?.label}   placeholder="Label (e.g. PREMIUM WEDDING CINEMA)" onChange={(v) => onChange("hero.label",   v)} />
          <EditableText tag="h2" className="sc-field--heading" value={config.hero?.heading} placeholder="Heading" onChange={(v) => onChange("hero.heading", v)} />
          <EditableText className="sc-field--sm" value={config.hero?.subtext} placeholder="Sub-text"              onChange={(v) => onChange("hero.subtext", v)} />
        </div>
      </div>

      {/* FEATURED FILM */}
      <div className="sc-section">
        <div className="sc-section__label">Featured Film (Hero highlight)</div>
        <VideoSlot
          src={config.hero?.feature?.video}
          onPick={() => setVideoDrawer({ path: "hero.feature.video" })}
          label="Click to pick featured video"
        />
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.hero?.feature?.couple}   placeholder="Couple name (e.g. Aarav & Jiya)"                     onChange={(v) => onChange("hero.feature.couple",   v)} />
          <EditableText className="sc-field--xs" value={config.hero?.feature?.location} placeholder="Location & year (e.g. JAIPUR • 2024)"               onChange={(v) => onChange("hero.feature.location", v)} />
          <EditableText className="sc-field--xs" value={config.hero?.feature?.trailer}  placeholder="Trailer button text (e.g. WATCH TRAILER (4:20))"    onChange={(v) => onChange("hero.feature.trailer",  v)} />
        </div>
      </div>

      {/* GALLERY HEADER */}
      <div className="sc-section">
        <div className="sc-section__label">Film Gallery — Header</div>
        <div className="sc-section__fields">
          <EditableText tag="h3" className="sc-field--heading" value={config.gallery?.heading} placeholder="Gallery heading (e.g. Film Gallery)" onChange={(v) => onChange("gallery.heading", v)} />
          <EditableText className="sc-field--xs" value={config.gallery?.cta} placeholder="CTA text (e.g. View All Stories)" onChange={(v) => onChange("gallery.cta", v)} />
        </div>
      </div>

      {/* FILMS */}
      <div className="sc-section">
        <div className="sc-section__label">Films</div>
        <div className="sc-films-grid">
          {films.map((film, i) => (
            <div key={i} className="sc-film-card">
              <div className="sc-film-card__header">
                <span className="sc-film-card__num">Film {i + 1}</span>
                <button className="sc-film-card__remove" onClick={() => removeFilm(i)} title="Remove film">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="12" height="12">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <VideoSlot
                src={film.video}
                onPick={() => setVideoDrawer({ path: `gallery.films.${i}.video` })}
                label={`Pick video for Film ${i + 1}`}
              />
              <div className="sc-film-card__fields">
                <EditableText className="sc-field--xs" value={film.tag}   placeholder="Tag (e.g. CINEMATIC SHORT)" onChange={(v) => updateFilm(i, "tag",   v)} />
                <EditableText className="sc-field--xs" value={film.title} placeholder="Film title"                 onChange={(v) => updateFilm(i, "title", v)} />
                <div className="sc-film-span-row">
                  <label className="sc-inline-label" style={{ marginBottom: 0 }}>Span</label>
                  <select
                    className="sc-span-select"
                    value={film.span || "normal"}
                    onChange={(e) => updateFilm(i, "span", e.target.value)}
                  >
                    <option value="normal">Normal</option>
                    <option value="wide">Wide</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
          <button className="sc-add-btn" onClick={addFilm}>+ Add Film</button>
        </div>
      </div>

      {/* SIGNATURE */}
      <div className="sc-section">
        <div className="sc-section__label">Signature / CTA</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.signature?.label}   placeholder="Label"          onChange={(v) => onChange("signature.label",   v)} />
          <EditableText tag="h3" className="sc-field--heading" value={config.signature?.heading} placeholder="Heading" onChange={(v) => onChange("signature.heading", v)} />
          <EditableText className="sc-field--sm" value={config.signature?.cta}     placeholder="CTA button text" onChange={(v) => onChange("signature.cta",     v)} />
        </div>
      </div>

      <VideoPickerDrawer
        open={!!videoDrawer}
        onClose={() => setVideoDrawer(null)}
        onSelect={(url) => {
          const path = videoDrawer.path;
          // Handle gallery.films.N.video pattern
          const parts = path.split(".");
          if (parts[0] === "gallery" && parts[1] === "films" && !isNaN(parts[2])) {
            const idx   = Number(parts[2]);
            const updated = films.map((f, i) => i === idx ? { ...f, video: url } : f);
            onChange("gallery.films", updated);
          } else {
            onChange(path, url);
          }
        }}
      />
    </div>
  );
}

// ── Outdoor Video editor ───────────────────────────────────────────────────────
function OutdoorVideoEditor({ config, onChange }) {
  const [videoDrawer, setVideoDrawer] = useState(null);

  const films = config.gallery?.films || [];

  const updateFilm = (i, field, val) => {
    const updated = films.map((f, idx) => idx === i ? { ...f, [field]: val } : f);
    onChange("gallery.films", updated);
  };

  const addFilm = () => {
    onChange("gallery.films", [
      ...films,
      { id: Date.now(), video: "", tag: "", title: "", span: "normal" },
    ]);
  };

  const removeFilm = (i) => {
    onChange("gallery.films", films.filter((_, idx) => idx !== i));
  };

  return (
    <div className="sc-editor">

      {/* HERO */}
      <div className="sc-section">
        <div className="sc-section__label">Hero</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.hero?.label}   placeholder="Label (e.g. OUTDOOR CINEMA)" onChange={(v) => onChange("hero.label",   v)} />
          <EditableText tag="h2" className="sc-field--heading" value={config.hero?.heading} placeholder="Heading"    onChange={(v) => onChange("hero.heading", v)} />
          <EditableText className="sc-field--sm" value={config.hero?.subtext || config.hero?.sub}     placeholder="Sub-text"                  onChange={(v) => onChange("hero.subtext",     v)} />
        </div>
      </div>

      {/* FEATURED FILM */}
      <div className="sc-section">
        <div className="sc-section__label">Featured Film (Hero highlight)</div>
        <VideoSlot
          src={config.hero?.feature?.video}
          onPick={() => setVideoDrawer({ path: "hero.feature.video" })}
          label="Click to pick featured video"
        />
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.hero?.feature?.couple}   placeholder="Couple name (e.g. Aarav & Jiya)"                     onChange={(v) => onChange("hero.feature.couple",   v)} />
          <EditableText className="sc-field--xs" value={config.hero?.feature?.location} placeholder="Location & year (e.g. JAIPUR • 2024)"               onChange={(v) => onChange("hero.feature.location", v)} />
          <EditableText className="sc-field--xs" value={config.hero?.feature?.trailer}  placeholder="Trailer button text (e.g. WATCH TRAILER (4:20))"    onChange={(v) => onChange("hero.feature.trailer",  v)} />
        </div>
      </div>

      {/* GALLERY HEADER */}
      <div className="sc-section">
        <div className="sc-section__label">Film Gallery — Header</div>
        <div className="sc-section__fields">
          <EditableText tag="h3" className="sc-field--heading" value={config.gallery?.heading} placeholder="Gallery heading (e.g. Film Gallery)" onChange={(v) => onChange("gallery.heading", v)} />
          <EditableText className="sc-field--xs" value={config.gallery?.cta} placeholder="CTA text (e.g. View All Stories)" onChange={(v) => onChange("gallery.cta", v)} />
        </div>
      </div>

      {/* FILMS */}
      <div className="sc-section">
        <div className="sc-section__label">Films</div>
        <div className="sc-films-grid">
          {films.map((film, i) => (
            <div key={i} className="sc-film-card">
              <div className="sc-film-card__header">
                <span className="sc-film-card__num">Film {i + 1}</span>
                <button className="sc-film-card__remove" onClick={() => removeFilm(i)} title="Remove film">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="12" height="12">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <VideoSlot
                src={film.video}
                onPick={() => setVideoDrawer({ path: `gallery.films.${i}.video` })}
                label={`Pick video for Film ${i + 1}`}
              />
              <div className="sc-film-card__fields">
                <EditableText className="sc-field--xs" value={film.tag}   placeholder="Tag (e.g. CINEMATIC SHORT)" onChange={(v) => updateFilm(i, "tag",   v)} />
                <EditableText className="sc-field--xs" value={film.title} placeholder="Film title"                 onChange={(v) => updateFilm(i, "title", v)} />
                <div className="sc-film-span-row">
                  <label className="sc-inline-label" style={{ marginBottom: 0 }}>Span</label>
                  <select
                    className="sc-span-select"
                    value={film.span || "normal"}
                    onChange={(e) => updateFilm(i, "span", e.target.value)}
                  >
                    <option value="normal">Normal</option>
                    <option value="wide">Wide</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
          <button className="sc-add-btn" onClick={addFilm}>+ Add Film</button>
        </div>
      </div>

      {/* SIGNATURE */}
      <div className="sc-section">
        <div className="sc-section__label">Signature / CTA</div>
        <div className="sc-section__fields">
          <EditableText className="sc-field--xs" value={config.signature?.label}   placeholder="Label"          onChange={(v) => onChange("signature.label",   v)} />
          <EditableText tag="h3" className="sc-field--heading" value={config.signature?.heading} placeholder="Heading" onChange={(v) => onChange("signature.heading", v)} />
          <EditableText className="sc-field--sm" value={config.signature?.cta}     placeholder="CTA button text" onChange={(v) => onChange("signature.cta",     v)} />
        </div>
      </div>

      <VideoPickerDrawer
        open={!!videoDrawer}
        onClose={() => setVideoDrawer(null)}
        onSelect={(url) => {
          const path = videoDrawer.path;
          // Handle gallery.films.N.video pattern
          const parts = path.split(".");
          if (parts[0] === "gallery" && parts[1] === "films" && !isNaN(parts[2])) {
            const idx   = Number(parts[2]);
            const updated = films.map((f, i) => i === idx ? { ...f, video: url } : f);
            onChange("gallery.films", updated);
          } else {
            onChange(path, url);
          }
        }}
      />
    </div>
  );
}

const DEFAULT_CONFIGS = {
  home: {
    hero: {
      heading: "CAPTURING THE ESSENCE\nOF LIGHT & MOMENT.",
      cta: "VIEW PORTFOLIOS",
      image: "",
    },
    services: [
      { id: 1, title: "Engagement", path: "/engagement", description: "Capturing the romantic beginning and promise of your journey together with timeless elegance.", image: "" },
      { id: 2, title: "Ceremonies", path: "/wedding",    description: "Narrative-driven visual storytelling for high-end traditional unions.", image: "" },
      { id: 3, title: "Outdoor",    path: "/outdoor",    description: "Documentary-style pre-wedding coverage in breathtaking natural landscapes.", image: "" },
      { id: 4, title: "Birthday",   path: "/birthday",   description: "Vibrant, joyous moments and milestone birthday celebrations captured in full color and emotion.", image: "" },
    ],
    portfolio: {
      label: "SELECTED WORKS",
      heading: "A Glimpse into our Vision",
      images: ["", "", "", ""],    },
    testimonials: [
      { id: 1, quote: "", name: "Couple Name", role: "Wedding at City, Year" },
      { id: 2, quote: "", name: "Couple Name", role: "Outdoor Shoot at City, Year" },
      { id: 3, quote: "", name: "Couple Name", role: "Wedding Film at City, Year" },
    ],
  },
  wedding: {
    hero: { label: "PORTFOLIO", heading: "Timeless Love, Captured with Intention.", image: "", sub: "Every frame, a memory. Every moment, intentional.", badge: "Wedding Portfolio", floatTag: "Fine Art Wedding Photography" },
    story: { heading: "Preserving Eternal\nMoments", body: "", quote: "" },
    gallery: [],
    signature: { label: "YOUR STORY", heading: "Crafting a visual legacy that transcends time.", cta: "Book a Wedding Session" },
  },
  outdoor: {
    hero: { label: "PORTFOLIO", heading: "Where Nature Frames\nEvery Story.", image: "", sub: "Open skies, golden light, and love without boundaries.", badge: "Outdoor Portfolio", floatTag: "Destination & Nature Photography" },
    story: { heading: "Beyond the Walls,\nInto the Wild", body: "", quote: "" },
    gallery: [],
    signature: { label: "YOUR STORY", heading: "The world is your backdrop. Let us tell your story.", cta: "Book a Outdoor Session" },
  },
  wedding_video: {
    hero: {
      label: "PREMIUM WEDDING CINEMA",
      heading: "Moving Images,\nEternal Memories.",
      subtext: "Capturing the rhythm, the sound, and the breath of your most sacred union with cinematic precision.",
      feature: { image: "", video: "", couple: "", location: "", trailer: "WATCH TRAILER" },
    },
    gallery: {
      heading: "Film Gallery",
      cta: "View All Stories",
      films: [],
    },
    signature: { label: "YOUR STORY", heading: "Crafting a visual legacy that transcends time.", cta: "Book a Wedding Film" },
  },
  outdoor_video: {
    hero: {
      label: "OUTDOOR CINEMA",
      heading: "Beyond the Frame,\nInto the Wild.",
      subtext: "Open skies, golden light, and love — captured in motion.",
      feature: { image: "", video: "", couple: "", location: "", trailer: "WATCH TRAILER" },
    },
    gallery: {
      heading: "Film Gallery",
      cta: "View All Stories",
      films: [],
    },
    signature: { label: "YOUR STORY", heading: "The world is your backdrop. Let us tell your story in motion.", cta: "Book an Outdoor Film" },
  },
  engagement: {
    hero: { label: "ENGAGEMENT", heading: "Where Forever\nBegins.", image: "", sub: "Cherishing the joy, love, and anticipation of your new chapter.", badge: "Engagement Collection", floatTag: "Engagement Photography" },
    story: { heading: "A Celebration of\nPromise & Passion", body: "", quote: "" },
    gallery: [],
    signature: { label: "YOUR STORY", heading: "Every couple deserves to remember how their story began.", cta: "Book an Engagement Session" },
  },
  birthday: {
    hero: { label: "BIRTHDAY", heading: "Milestones, Joy\nand Unforgettable Memories.", image: "", sub: "Capturing every smile, laugh, and celebration of life.", badge: "Birthday Collection", floatTag: "Event & Birthday Photography" },
    story: { heading: "Celebrating Life's\nPrecious Moments", body: "", quote: "" },
    gallery: [],
    signature: { label: "YOUR STORY", heading: "Make your birthday memories last a lifetime.", cta: "Book a Birthday Session" },
  },
  footer: {
    logo:      "RESH PHOTOGRAPHY",
    tagline:   "Preserving moments with intentionality and light. Specializing in high-end destination weddings worldwide.",
    contact:   { email: "shsure480@gmail.com", links: [] },
    social:    { label: "SOCIAL",  links: [{ label: "Instagram", href: "https://www.instagram.com/resh_photography._" }] },
    studio:    { label: "STUDIO",  links: [{ label: "About Us",  href: "/about" }] },
    copyright: "© 2026 Resh Photography Studio. All rights reserved.",
  },
  about: {
    hero: { tag: "THE STUDIO NARRATIVE", heading: "Preserving the", headingItalic: "Sanctity", headingSuffix: "of Moments.", body: "", photographerImage: "", photographerName: "" },
    vision: {
      image: "",
      heading: "The Vision",
      body: "",
      points: [
        ...DEFAULT_ABOUT_VISION_POINTS,
      ],
    },
    process: { tag: "METHODOLOGY", heading: "Our Process", steps: [
      { num: "01", title: "Immersion",   desc: "" },
      { num: "02", title: "Observation", desc: "" },
      { num: "03", title: "Refinement",  desc: "" },
    ]},
    quote: { text: "", author: "AT RESHPHOTOGRAPHY, FOUNDER & LEAD PHOTOGRAPHER" },
  },
  contact: {
    image: "", imageTag: "MOMENTS PRESERVED", imageHeading: "Timeless artistry for your most cherished stories.",
    heading: "Get in touch.", subtext: "",
    email: "", phone: "", address: "", mapUrl: "",
    socials: [],
  },
};

// ─── Screen list ──────────────────────────────────────────────────────────────
const CLIENT_SCREENS = [
  { key: "home",          label: "Home",               description: "Hero · Services · Portfolio · Testimonials" },
  { key: "wedding",       label: "Wedding",             description: "Hero · Story · Gallery · Signature" },
  { key: "wedding_video", label: "Wedding Video",       description: "Hero · Featured Film · Film Gallery · Signature" },
  { key: "outdoor",       label: "Outdoor",             description: "Hero · Story · Gallery · Signature" },
  { key: "outdoor_video", label: "Outdoor Video",       description: "Hero · Film Gallery · Signature" },
  { key: "engagement",    label: "Engagement",          description: "Hero · Story · Gallery · Signature" },
  { key: "birthday",      label: "Birthday",            description: "Hero · Story · Gallery · Signature" },
  { key: "footer",        label: "Footer",              description: "Brand · Contact · Copyright" },
  { key: "about",         label: "About Us",            description: "Hero · Vision · Process · Quote" },
  { key: "contact",       label: "Contact Us",          description: "Image · Info · Map · Socials" },
];

// ─── Screen Config Editor page ────────────────────────────────────────────────
function ScreenEditor({ screenKey, onBack, showSnack }) {
  const screen = CLIENT_SCREENS.find((s) => s.key === screenKey);
  const [config, setConfig]   = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [docId, setDocId]     = useState(null); // existing MongoDB _id

  // Load from DB
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/entities/filter/screen_config`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(
          {
              "start": 0,
              "end": 1000,
              "filter": [
                  {
                      "clause": "AND",
                      "conditions": [
                          { "column": "screen_key", "operator": "EQUALS", "value": screenKey }
                      ]
                  }
              ],
          }
      ),
    })
      .then((r) => r.json())
      .then((d) => {
        const rows = d?.data?.[0]?.response ?? [];
        let loaded = DEFAULT_CONFIGS[screenKey] || {};
        if (rows.length > 0) {
          setDocId(rows[0]._id);
          loaded = rows[0].config_data || DEFAULT_CONFIGS[screenKey] || {};
        }
        setConfig(loaded);
        setOriginalConfig(loaded);
      })
      .catch(() => {
        const loaded = DEFAULT_CONFIGS[screenKey] || {};
        setConfig(loaded);
        setOriginalConfig(loaded);
      })
      .finally(() => setLoading(false));
  }, [screenKey]);

  // Deep set helper
  const handleChange = useCallback((path, value) => {
    setConfig((prev) => set(prev, path, value));
  }, []);

  const handleDiscard = () => {
    setConfig(originalConfig);
    showSnack("Changes discarded.", "success");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url  = docId
        ? `${API_BASE}/entities/screen_config/${docId}`
        : `${API_BASE}/entities/screen_config`;
      const body = { screen_key: screenKey, config_data: config, status: "Active" };
      const res  = await fetch(url, { method: docId ? "PUT" : "POST", headers: authHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || data.message || "Save failed");
      if (!docId && data.data?.["insert ID"]) setDocId(data.data["insert ID"]);
      setOriginalConfig(config);
      showSnack("Changes saved.", "success");
    } catch (err) { showSnack(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="sc-editor-page">
      {/* Editor header */}
      <div className="sc-editor-header">
        <button className="sc-back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          All Screens
        </button>
        <div>
          <h1 className="sc-editor-title">{screen?.label}</h1>
          <p className="sc-editor-sub">{screen?.description}</p>
        </div>
        <div className="sc-editor-actions">
          <button className="sc-discard-btn" onClick={handleDiscard} disabled={saving || loading || JSON.stringify(config) === JSON.stringify(originalConfig)}>
            Discard
          </button>
          <button className="sc-save-btn" onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Editor body */}
      <div style={{ position: "relative", minHeight: "300px", flex: 1, display: "flex", flexDirection: "column" }}>
        {loading ? (
          <div className="app-loading-overlay">
            <div className="app-spinner"></div>
          </div>
        ) : screenKey === "home" ? (
          <HomeEditor config={config} onChange={handleChange} />
        ) : screenKey === "footer" ? (
          <FooterEditor config={config} onChange={handleChange} />
        ) : screenKey === "about" ? (
          <AboutEditor config={config} onChange={handleChange} />
        ) : screenKey === "contact" ? (
          <ContactEditor config={config} onChange={handleChange} />
        ) : screenKey === "wedding_video" ? (
          <WeddingVideoEditor config={config} onChange={handleChange} />
        ) : screenKey === "outdoor_video" ? (
          <OutdoorVideoEditor config={config} onChange={handleChange} />
        ) : (
          <PortalEditor config={config} onChange={handleChange} screenKey={screenKey} />
        )}
      </div>
    </div>
  );
}

// ─── Screen list view ─────────────────────────────────────────────────────────
function ScreenList({ onSelect }) {
  const ICONS = {
    home:          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z" /><path d="M9 21V12h6v9" /></svg>,
    wedding:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M12 21.7C5.4 15.5 2 11.2 2 7.8 2 4.7 4.7 2 7.8 2c1.7 0 3.4.9 4.2 2.3C12.8 2.9 14.5 2 16.2 2 19.3 2 22 4.7 22 7.8c0 3.4-3.4 7.7-10 13.9z" /></svg>,
    wedding_video: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M12 21.7C5.4 15.5 2 11.2 2 7.8 2 4.7 4.7 2 7.8 2c1.7 0 3.4.9 4.2 2.3C12.8 2.9 14.5 2 16.2 2 19.3 2 22 4.7 22 7.8c0 3.4-3.4 7.7-10 13.9z" /><polygon points="10 9 16 12 10 15 10 9" fill="currentColor" stroke="none" /></svg>,
    outdoor:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M12 3a6 6 0 0 1 0 12 6 6 0 0 1 0-12z" /><path d="M3 21c0-4.97 4-9 9-9s9 4.03 9 9" /></svg>,
    outdoor_video: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M12 3a6 6 0 0 1 0 12 6 6 0 0 1 0-12z" /><path d="M3 21c0-4.97 4-9 9-9s9 4.03 9 9" /><polygon points="10 9 16 12 10 15 10 9" fill="currentColor" stroke="none" /></svg>,
    engagement:    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M12 21.7C5.4 15.5 2 11.2 2 7.8 2 4.7 4.7 2 7.8 2c1.7 0 3.4.9 4.2 2.3C12.8 2.9 14.5 2 16.2 2 19.3 2 22 4.7 22 7.8c0 3.4-3.4 7.7-10 13.9z" /></svg>,
    birthday:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    bride_portal:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><circle cx="12" cy="7" r="4" /><path d="M5.5 21a7 7 0 0 1 13 0" /></svg>,
    groom_portal:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><circle cx="12" cy="7" r="4" /><path d="M5.5 21a7 7 0 0 1 13 0" /></svg>,
    footer:        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><rect x="2" y="14" width="20" height="7" rx="1" /><line x1="2" y1="10" x2="22" y2="10" /></svg>,
    about:         <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
    contact:       <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" width="26" height="26"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>,
  };

  return (
    <div className="sc-list">
      <div className="sc-page__header">
        <div>
          <h1 className="sc-page__title">Screen Config</h1>
          <p className="sc-page__subtitle">Select a screen to edit its content, images and text.</p>
        </div>
      </div>
      <div className="sc-grid">
        {CLIENT_SCREENS.map((screen) => (
          <button key={screen.key} className="sc-card" onClick={() => onSelect(screen.key)}>
            <div className="sc-card__icon">{ICONS[screen.key]}</div>
            <div className="sc-card__body">
              <div className="sc-card__head">
                <span className="sc-card__label">{screen.label}</span>
              </div>
              <p className="sc-card__desc">{screen.description}</p>
            </div>
            <svg className="sc-card__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="16" height="16">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Root page ────────────────────────────────────────────────────────────────
export default function ScreenConfig() {
  const [activeScreen, setActiveScreen] = useState(null);
  const { snack, showSnack, dismissSnack } = useSnackbar();

  return (
    <AdminLayout>
      {activeScreen ? (
        <ScreenEditor
          screenKey={activeScreen}
          onBack={() => setActiveScreen(null)}
          showSnack={showSnack}
        />
      ) : (
        <ScreenList onSelect={setActiveScreen} />
      )}
      <Snackbar snack={snack} onDismiss={dismissSnack} />
    </AdminLayout>
  );
}
